import React, { useState, useMemo } from 'react';
import useModalAccessibility from '../../hooks/useModalAccessibility';
import PasswordInput from '../PasswordInput';
import { formatPhone, normalizePhoneInput } from '../../utils/formatters';

const PasswordStrengthIndicator = ({ password }) => {
  const analysis = useMemo(() => {
    const checks = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    const score = Object.values(checks).filter(Boolean).length;
    let strength = 'weak';
    let color = 'var(--danger)';

    if (score >= 4) {
      strength = 'strong';
      color = 'var(--success)';
    } else if (score >= 3) {
      strength = 'medium';
      color = 'var(--warning)';
    }

    return { checks, score, strength, color };
  }, [password]);

  if (!password) return null;

  return (
    <div className="password-strength">
      <div className="strength-bar-container">
        <div
          className={`strength-bar ${analysis.strength}`}
          style={{
            width: `${(analysis.score / 5) * 100}%`,
            backgroundColor: analysis.color
          }}
        />
      </div>
      <span className={`strength-label ${analysis.strength}`}>
        {analysis.strength.charAt(0).toUpperCase() + analysis.strength.slice(1)}
      </span>
      <ul className="password-requirements">
        <li className={analysis.checks.minLength ? 'met' : ''}>
          {analysis.checks.minLength ? '✓' : '○'} At least 8 characters
        </li>
        <li className={analysis.checks.hasUppercase ? 'met' : ''}>
          {analysis.checks.hasUppercase ? '✓' : '○'} Uppercase letter
        </li>
        <li className={analysis.checks.hasLowercase ? 'met' : ''}>
          {analysis.checks.hasLowercase ? '✓' : '○'} Lowercase letter
        </li>
        <li className={analysis.checks.hasNumber ? 'met' : ''}>
          {analysis.checks.hasNumber ? '✓' : '○'} Number
        </li>
        <li className={analysis.checks.hasSpecial ? 'met' : ''}>
          {analysis.checks.hasSpecial ? '✓' : '○'} Special character
        </li>
      </ul>
    </div>
  );
};

const CreateUserModal = ({ onClose, onSave }) => {
  const modalRef = useModalAccessibility(onClose);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: 'user',
    tlc_license_number: '',
    years_experience: '',
    fleet_name: '',
    medallion_numbers: '',
    garage_location: '',
    bio: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = name === 'phone' ? formatPhone(value) : value;
    setFormData(prev => ({ ...prev, [name]: next }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phoneDigits = normalizePhoneInput(formData.phone);
    if (phoneDigits.length !== 10) {
      alert('Phone number must be 10 digits');
      return;
    }
    setLoading(true);
    try {
      await onSave({ ...formData, phone: phoneDigits });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        className="edit-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-user-title"
      >
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          aria-label="Close dialog"
        >
          &times;
        </button>
        <h2 id="create-user-title">Create User</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Password *</label>
            <PasswordInput
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
            />
            <PasswordStrengthIndicator password={formData.password} />
          </div>

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Phone *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(212) 555-1234"
              required
            />
          </div>

          <div className="form-group">
            <label>Role *</label>
            <select name="role" value={formData.role} onChange={handleChange} required>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {formData.role !== 'admin' && (
            <>
              <div className="form-group">
                <label>TLC Licence No#</label>
                <input
                  type="text"
                  name="tlc_license_number"
                  value={formData.tlc_license_number}
                  onChange={(e) => { const val = e.target.value.replace(/\D/g, ''); handleChange({ target: { name: 'tlc_license_number', value: val } }); }}
                  placeholder="Numbers only"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
              <div className="form-group">
                <label>Years Experience</label>
                <input
                  type="number"
                  name="years_experience"
                  value={formData.years_experience}
                  onChange={handleChange}
                  min="0"
                  max="50"
                />
              </div>
              <div className="form-group">
                <label>Business Name</label>
                <input
                  type="text"
                  name="fleet_name"
                  value={formData.fleet_name}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Medallion Numbers</label>
                <input
                  type="text"
                  name="medallion_numbers"
                  value={formData.medallion_numbers}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Garage Location</label>
                <input
                  type="text"
                  name="garage_location"
                  value={formData.garage_location}
                  onChange={handleChange}
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Bio</label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
